import { useEffect, useId, useRef } from 'react';
import mermaid from 'mermaid';
import { buildMermaidThemeVariables, adaptDiagramDirection } from '@/shared/utils/mermaidTheme';
import { ScrollableOverflow } from '@/shared/components/ScrollableOverflow';

let idCounter = 0;
function nextDiagramId(): string {
  return `mermaid-diagram-${++idCounter}`;
}

interface DiagramRendererProps {
  /** Mermaid diagram syntax to render. */
  syntax: string;
  /** Unique DOM id for the diagram element. Auto-generated if omitted. */
  id?: string;
  /** Additional class names for the container div. */
  className?: string;
  /** Called after the SVG has been injected into the DOM. */
  onRendered?: (container: HTMLDivElement) => void;
  /** Re-render when the theme (dark/light) changes. Default: true. */
  watchTheme?: boolean;
}

function applyRenderedSvg(container: HTMLDivElement, svg: string): void {
  // Safe: mermaid renders with securityLevel:'strict' which sanitises the SVG
  // output before returning it. We never pass user-authored HTML here.
  container.innerHTML = svg;

  const svgEl = container.querySelector('svg');
  if (svgEl) {
    svgEl.style.height = 'auto';
  }
}

export default function DiagramRenderer({
  syntax,
  id,
  className,
  onRendered,
  watchTheme = true,
}: DiagramRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  // Stable diagram id for the lifetime of this component instance.
  const reactId = useId();
  const diagramId = useRef<string>(id ?? `mermaid-${reactId.replace(/:/g, '')}-${nextDiagramId()}`);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;

    async function render() {
      if (!container || cancelled) return;

      // Hide any previous error.
      if (errorRef.current) {
        errorRef.current.style.display = 'none';
      }

      const tokenSource = container.closest('[data-interface]') ?? container;
      const themeVariables = buildMermaidThemeVariables(tokenSource);

      mermaid.initialize({
        startOnLoad: false,
        theme: 'base',
        themeVariables,
        securityLevel: 'strict',
        flowchart: { htmlLabels: true, useMaxWidth: false, curve: 'basis' },
      });

      try {
        const adaptedSyntax = adaptDiagramDirection(syntax);
        const { svg, bindFunctions } = await mermaid.render(diagramId.current, adaptedSyntax);

        if (cancelled || !container) return;

        applyRenderedSvg(container, svg);

        // Bind interaction handlers (e.g. click events) if present.
        bindFunctions?.(container);

        onRendered?.(container);
      } catch (err) {
        if (cancelled) return;
        container.innerHTML = '';
        if (errorRef.current) {
          const message = err instanceof Error ? err.message : String(err);
          errorRef.current.textContent = `Diagram error: ${message}`;
          errorRef.current.style.display = 'block';
        }
      }
    }

    void render();

    return () => {
      cancelled = true;
    };
    // onRendered is intentionally excluded — callers may pass inline functions and
    // re-rendering on every parent render would be disruptive. The callback is
    // captured inside the async closure at call time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syntax]);

  // Re-render when the dark/light theme class changes on <html>.
  useEffect(() => {
    if (!watchTheme) return;

    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some(
        (m) => m.type === 'attributes' && m.attributeName === 'class'
      );
      if (!relevant) return;

      const container = containerRef.current;
      if (!container) return;

      const tokenSource = container.closest('[data-interface]') ?? container;
      const themeVariables = buildMermaidThemeVariables(tokenSource);

      mermaid.initialize({
        startOnLoad: false,
        theme: 'base',
        themeVariables,
        securityLevel: 'strict',
        flowchart: { htmlLabels: true, useMaxWidth: false, curve: 'basis' },
      });

      const adaptedSyntax = adaptDiagramDirection(syntax);
      void mermaid.render(diagramId.current, adaptedSyntax).then(({ svg, bindFunctions }) => {
        if (!container) return;
        applyRenderedSvg(container, svg);
        bindFunctions?.(container);
        onRendered?.(container);
      });
    });

    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();
    };
  }, [syntax, watchTheme, onRendered]);

  return (
    <ScrollableOverflow className={className}>
      <div ref={containerRef} />
      <div
        ref={errorRef}
        style={{
          display: 'none',
          padding: '12px 16px',
          borderRadius: 8,
          border: '1px solid var(--color-error, var(--f-error))',
          background: 'var(--color-error-subtle, var(--f-error-subtle, var(--f-bg-raised)))',
          color: 'var(--color-error, var(--f-error))',
          fontSize: 'var(--text-sm, var(--f-text-sm))',
          fontFamily: 'var(--font-sans, var(--f-font-sans))',
        }}
      />
    </ScrollableOverflow>
  );
}
